// SubscriptionState.jsx (JS, not TS)
import React from "react";
import { Alert, AlertTitle, Button, Stack, Typography } from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  CreditCardOff,
  Warning as WarningIcon,
  LaunchOutlined,
  Refresh,
} from "@mui/icons-material";
import { DateTime } from "luxon";

// Helper: derive a UI-friendly state from your record
function deriveState(sub) {
  // Normalize
  const status = (sub?.status || "").toLowerCase(); // e.g., "ACTIVE" -> "active"
  const now = DateTime.utc();
  const periodEnd = sub?.current_period_end
    ? DateTime.fromISO(sub.current_period_end)
    : null;
  const cancelAt = sub?.cancel_at ? DateTime.fromISO(sub.cancel_at) : null;

  // Stripe-like statuses you might persist: active, canceled, past_due, unpaid
  // Special case: active but will cancel at period end
  if (status === "active" && cancelAt && periodEnd && cancelAt.equals(periodEnd)) {
    return "canceling"; // Active until period end
  }
  if (status === "trial") return "trial";
  if (status === "active") return "active";
  if (status === "past_due") return "past_due";
  if (status === "unpaid") return "unpaid";
  if (status === "canceled") return "canceled";
  return "unknown";
}

// Central config: maps derived state -> UI
const STATUS_UI = {
 trial: {
    severity: "success",
    title: "Trial active",
    message: () => {
        return `Limited features are enabled. Select a new subscription to upgrade!`;
    },
    icon: null,
    primaryAction: null,
    secondaryAction: null,
  },
  active: {
    severity: "success",
    title: "Subscription Active",
    message: (sub) => {
      const end = sub?.current_period_end
        ? DateTime.fromISO(sub.current_period_end).toLocaleString(DateTime.DATE_MED)
        : "—";
      return `All features are available. Next renewal on ${end}.`;
    },
    icon: <CheckCircle fontSize="inherit" />,
    primaryAction: { label: "Manage in Stripe", icon: <LaunchOutlined /> },
    secondaryAction: null,
  },
  canceling: {
    severity: "warning",
    title: "Cancellation Scheduled",
    message: (sub) => {
      const end = sub?.current_period_end
        ? DateTime.fromISO(sub.current_period_end).toLocaleString(DateTime.DATE_MED)
        : "the period end";
      return `Your access remains active until ${end}. You can resume to keep your plan.`;
    },
    icon: <WarningIcon fontSize="inherit" />,
    primaryAction: { label: "Resume / Manage", icon: <Refresh /> },
    secondaryAction: { label: "Open Stripe", icon: <LaunchOutlined /> },
  },
  past_due: {
    severity: "error",
    title: "Payment Past Due",
    message:
      () =>
        "We couldn’t collect payment for the latest invoice. Update your payment method to avoid interruption.",
    icon: <WarningIcon fontSize="inherit" />,
    primaryAction: { label: "Update Payment", icon: <LaunchOutlined /> },
    secondaryAction: null,
  },
  unpaid: {
    severity: "error",
    title: "Payment Unpaid",
    message:
      () =>
        "Your subscription is unpaid. Pay the outstanding invoice or update your card to restore service.",
    icon: <CreditCardOff fontSize="inherit" />,
    primaryAction: { label: "Pay / Update Card", icon: <LaunchOutlined /> },
    secondaryAction: null,
  },
  canceled: {
    severity: "info",
    title: "Subscription Canceled",
    message:
      () =>
        "Your subscription has been canceled. You can reactivate anytime.",
    icon: <ErrorIcon fontSize="inherit" />,
    primaryAction: { label: "Reactivate", icon: <LaunchOutlined /> },
    secondaryAction: null,
  },
  unknown: {
    severity: "info",
    title: "Subscription",
    message: () => "Subscription status unavailable.",
    icon: <WarningIcon fontSize="inherit" />,
    primaryAction: { label: "Manage in Stripe", icon: <LaunchOutlined /> },
    secondaryAction: null,
  },
};

export default function SubscriptionState({
  sub,                 // your subscription object (like the JSON you posted)
  onManage,            // function (stripeCustomerId) => void (open portal)
  onSecondaryAction,   // optional alt handler
}) {
  const state = deriveState(sub);
  console.log(state)
  const cfg = STATUS_UI[state] || STATUS_UI.unknown;
  console.log(cfg)

  const stripeCustomerId = sub?.stripe_customer_id;

  const handlePrimary = () => {
    if (onManage) onManage(stripeCustomerId);
  };
  const handleSecondary = () => {
    if (onSecondaryAction) onSecondaryAction(stripeCustomerId);
    else if (onManage) onManage(stripeCustomerId);
  };

  return (
    <Alert
      sx={{ mb: 1 }}
      variant="outlined"
      severity={cfg.severity}
      icon={cfg.icon}
    >
      <AlertTitle>
        <Typography fontWeight="bold" variant="h6">
          {cfg.title}
        </Typography>
      </AlertTitle>

      <Typography variant="body1" sx={{ mb: 1 }}>
        {typeof cfg.message === "function" ? cfg.message(sub) : cfg.message}
      </Typography>

      <Stack direction="row" spacing={1}>
        {cfg.primaryAction && (
          <Button
            onClick={handlePrimary}
            variant="contained"
            endIcon={cfg.primaryAction.icon}
          >
            {cfg.primaryAction.label}
          </Button>
        )}
        {cfg.secondaryAction && (
          <Button
            color="inherit"
            onClick={handleSecondary}
            variant="text"
            endIcon={cfg.secondaryAction.icon}
          >
            {cfg.secondaryAction.label}
          </Button>
        )}
      </Stack>
    </Alert>
  );
}